/*
 * KeyRizzer - deferred 3D loading.
 *
 * model-viewer is a 913 KB library and each GLB is ~520 KB. Loading both
 * eagerly on every page cost phones 1.4 MB before anything was visible.
 *
 * Instead each 3D slot ships a poster image. The library and the model are
 * fetched only when the slot approaches the viewport, and on small screens
 * only if the section opts in - otherwise the poster simply stays, which is
 * what a decorative hero actually needs.
 */
(() => {
  const LIB = 'https://cdn.jsdelivr.net/npm/@google/model-viewer@3.5.0/dist/model-viewer.min.js';
  const SMALL = 750;

  let libPromise = null;
  const loadLib = () => {
    if (!libPromise) {
      libPromise = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.type = 'module';
        s.src = LIB;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      });
    }
    return libPromise;
  };

  function upgrade(host) {
    if (host.dataset.krDone) return;
    host.dataset.krDone = '1';

    loadLib().then(() => {
      const mv = document.createElement('model-viewer');
      mv.setAttribute('src', host.dataset.src);
      mv.setAttribute('alt', host.dataset.alt || '');
      mv.setAttribute('shadow-intensity', '0.9');
      mv.setAttribute('exposure', '1.15');
      mv.setAttribute('environment-image', 'neutral');
      if (host.dataset.poster) mv.setAttribute('poster', host.dataset.poster);
      if (host.dataset.cameraOrbit) mv.setAttribute('camera-orbit', host.dataset.cameraOrbit);
      if (host.dataset.interactive === 'true') {
        mv.setAttribute('camera-controls', '');
        mv.setAttribute('touch-action', 'pan-y');
      } else {
        mv.setAttribute('disable-zoom', '');
        mv.setAttribute('interaction-prompt', 'none');
      }
      if (host.dataset.autoRotate === 'true') {
        mv.setAttribute('auto-rotate', '');
        mv.setAttribute('auto-rotate-delay', '0');
        mv.setAttribute('rotation-per-second', (host.dataset.speed || '20') + 'deg');
      }
      if (host.dataset.ar === 'true') {
        mv.setAttribute('ar', '');
        mv.setAttribute('ar-modes', 'webxr scene-viewer quick-look');
      }

      mv.addEventListener('load', () => {
        const poster = host.querySelector('[data-kr-poster]');
        if (poster) poster.style.opacity = '0';
        host.dataset.krReady = '1';
        host.dispatchEvent(new CustomEvent('kr:model-ready'));
      });

      host.appendChild(mv);
    }).catch(() => {
      // Library blocked or offline: the poster stays, which is a fine outcome.
      host.dataset.krFailed = '1';
    });
  }

  const wanted = (host) => {
    if (window.innerWidth >= SMALL) return true;
    return host.dataset.mobile3d === 'true';
  };

  const hosts = Array.from(document.querySelectorAll('[data-kr-model-host]'));
  if (!hosts.length) return;

  if (!('IntersectionObserver' in window)) {
    hosts.filter(wanted).forEach(upgrade);
    return;
  }

  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      io.unobserve(e.target);
      if (wanted(e.target)) upgrade(e.target);
    });
  }, { rootMargin: '200px 0px' });

  hosts.forEach((h) => io.observe(h));

  // A visitor tapping the poster on a phone is asking for the 3D explicitly.
  hosts.forEach((h) => {
    h.addEventListener('click', () => {
      if (!h.dataset.krDone && h.dataset.tapToLoad !== 'false') upgrade(h);
    });
  });
})();
